import { useState } from "react";
import { Button } from "@ferrivia/ui";
import { useTracker } from "@ferrivia/auth-web";
import { useTranslation } from "@ferrivia/i18n";
import type { QuestionnaireSummaryListItem, ReadJourneyStep } from "@ferrivia/api-contract";
import { JourneySteps, type JourneyStepsCopy } from "../../../components/JourneySteps";
import { BookingSheet } from "./BookingSheet";

/* The Iron infusion tab's steps card: the three things the partner needs
 * before the remote appointment. The card itself is JourneySteps; this holds
 * the tab's copy, its analytics event and the booking explainer. */
export function InfusionSteps({
  steps,
  summaries,
  refetch,
}: {
  steps: ReadJourneyStep[];
  summaries?: readonly QuestionnaireSummaryListItem[];
  refetch: () => void;
}) {
  const { t } = useTranslation("client");
  const tracker = useTracker();
  const [bookingOpen, setBookingOpen] = useState(false);

  // Every t() key below is a literal: i18next-parser cannot follow a key
  // through a variable, so this is a registry of copy, not of key strings.
  const copy: JourneyStepsCopy = {
    eyebrow: t("infusion.steps.eyebrow", "Ennen lääkärin vastaanottoa"),
    title: t("infusion.steps.title", "Näin pääset alkuun"),
    steps: {
      labs: {
        label: t("infusion.steps.labs.label", "Verikokeet"),
        body: {
          todo: t(
            "infusion.steps.labs.todo",
            "Etävastaanotolle vaaditaan enintään kolme kuukautta vanhat verikokeet (ferritiini, herkkä CRP, hemoglobiini). Voit ostaa ne meiltä tai lisätä omat tuloksesi.",
          ),
          in_progress: t(
            "infusion.steps.labs.inProgress",
            "Laboratoriopakettisi on tilattu. Tämä vaihe täyttyy, kun näytteesi on otettu ja laboratorio on raportoinut sen.",
          ),
          done: t(
            "infusion.steps.labs.done",
            "Verikokeesi on otettu viimeisen kolmen kuukauden aikana, joten ne kelpaavat vastaanotolle.",
          ),
        },
        storeCta: t("infusion.steps.labs.storeCta", "Tilaa laboratoriopaketti"),
        uploadCta: t("infusion.steps.labs.uploadCta", "Lisää omat tulokset"),
      },
      screener: {
        label: t("infusion.steps.screener.label", "Esitietokysely"),
        body: {
          todo: t(
            "infusion.steps.screener.todo",
            "Täytä esitietokysely valmiiksi, jotta tietoja ei tarvitse kerätä vastaanotolla.",
          ),
          in_progress: t("infusion.steps.screener.inProgress", "Jatka esitietojen täyttämistä siitä mihin jäit."),
          done: t("infusion.steps.screener.done", "Esitietosi ovat koossa lääkärille."),
        },
      },
      booking: {
        label: t("infusion.steps.booking.label", "Varaa aika"),
        body: {
          locked: t(
            "infusion.steps.booking.locked",
            "Kun tulokset ja esitiedot ovat valmiina, voit varata etävastaanottoajan lääkärille.",
          ),
          todo: t(
            "infusion.steps.booking.todo",
            "Tarvittavat esitiedot ja verikokeet on nyt käyty läpi. Voit varata etävastaanottoajan lääkärille.",
          ),
        },
      },
    },
  };

  return (
    <>
      <JourneySteps
        steps={steps}
        summaries={summaries}
        refetch={refetch}
        copy={copy}
        track={(cta) => tracker.track("infusion.step_cta_clicked", { cta })}
        summaryPath="infusion"
        renderBooking={(variant) => (
          <Button
            variant={variant}
            onClick={() => {
              tracker.track("infusion.step_cta_clicked", { cta: "booking" });
              setBookingOpen(true);
            }}
          >
            {t("infusion.steps.booking.cta", "Katso, miten varaus toimii")}
          </Button>
        )}
      />
      <BookingSheet open={bookingOpen} onOpenChange={setBookingOpen} />
    </>
  );
}
